import React, { useEffect, useRef, useState } from 'react';

import classnames from 'classnames';

import useUniqueTabIds from '../hooks/useUniqueTabIds';
import { type TabsCardsProps } from '../Tabs';

import styles from './TabsCards.module.scss';

const TabsCards = ({
    items,
    activeKey,
    defaultActiveKey,
    onChange,
    destroyInactiveTabPane = false,
    tabBarStyle
}: Omit<TabsCardsProps, 'type'>) => {
    const lazyLoadedKeys = useRef(new Set());

    const isControlled = activeKey !== undefined;

    const [internalActiveKey, setInternalActiveKey] = useState(() => {
        if (isControlled) {
            return activeKey;
        }

        return defaultActiveKey || (items.length > 0 ? items[0].key : '');
    });

    const currentActiveKey = isControlled ? activeKey : internalActiveKey;
    lazyLoadedKeys.current.add(currentActiveKey);

    useEffect(() => {
        if (isControlled) {
            setInternalActiveKey(activeKey);
        }
    }, [activeKey]);

    const handleTabChange = (key: string) => {
        if (!isControlled) {
            setInternalActiveKey(key);
        }

        if (onChange) {
            onChange(key);
        }
    };

    const { getTabId, getPanelId } = useUniqueTabIds();

    return (
        <div className={classnames(styles.tabs)}>
            <div role="tablist" className={classnames(styles.tablist)} style={tabBarStyle}>
                {items.map(({ key, label, disabled, id }) => (
                    <button
                        key={key}
                        type="button"
                        role="tab"
                        aria-selected={currentActiveKey === key}
                        aria-controls={getPanelId(key)}
                        id={id || getTabId(key)}
                        disabled={disabled}
                        className={classnames(styles.tab, {
                            [styles.active]: currentActiveKey === key
                        })}
                        onClick={() => handleTabChange(key)}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {items.map(({ key, children, className, disablePadding, id }) => {
                if (!children) {
                    return null;
                }

                const shouldShowChildren = destroyInactiveTabPane
                    ? currentActiveKey === key
                    : lazyLoadedKeys.current.has(key);

                return (
                    <div
                        key={key}
                        role="tabpanel"
                        hidden={currentActiveKey !== key}
                        aria-labelledby={id || getTabId(key)}
                        id={getPanelId(key)}
                        className={classnames(
                            styles.tabpanel,
                            { [styles.disablePadding]: disablePadding },
                            className
                        )}
                    >
                        {shouldShowChildren && children}
                    </div>
                );
            })}
        </div>
    );
};

export default TabsCards;
